import { useEffect, useState } from "react";
import { LocalizedLink as Link } from "@/components/routing/LocalizedLink";
import { useTranslation } from "react-i18next";
import { PublicShell } from "@/components/layouts/PublicShell";
import { Seo } from "@/components/seo/Seo";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useLocalizedField } from "@/hooks/useLocalizedField";
import { Skeleton } from "@/components/ui/skeleton";
import { Target, Zap, Users as UsersIcon, Heart, ArrowRight } from "lucide-react";
import { SectionHeader } from "@/components/ui/section-header";
import { Reveal } from "@/components/motion/Reveal";

interface ProviderRow {
  user_id: string;
  headline_en: string | null;
  headline_es: string | null;
  headline_fr: string | null;
  headline_pt: string | null;
  bio_en: string | null;
  bio_es: string | null;
  bio_fr: string | null;
  bio_pt: string | null;
  expertise_areas: string[] | null;
  profile?: { first_name: string | null; last_name: string | null; avatar_url: string | null; city: string | null; country: string | null } | null;
}

export default function About() {
  const { t } = useTranslation();
  const pickHeadline = useLocalizedField<ProviderRow>();
  const pickBio = useLocalizedField<ProviderRow>();
  const [providers, setProviders] = useState<ProviderRow[] | null>(null);

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("provider_profiles")
        .select("user_id, headline_en, headline_es, headline_fr, headline_pt, bio_en, bio_es, bio_fr, bio_pt, expertise_areas")
        .eq("published", true)
        .limit(6);
      if (!data) { setProviders([]); return; }
      const ids = data.map((d) => d.user_id);
      let profilesMap = new Map<string, ProviderRow["profile"]>();
      if (ids.length) {
        const { data: profs } = await supabase
          .from("profiles")
          .select("id, first_name, last_name, avatar_url, city, country")
          .in("id", ids);
        profs?.forEach((p) => profilesMap.set(p.id, { first_name: p.first_name, last_name: p.last_name, avatar_url: p.avatar_url, city: p.city, country: p.country }));
      }
      setProviders(data.map((d) => ({ ...d, profile: profilesMap.get(d.user_id) ?? null })));
    })();
  }, []);

  // Operator quotes (sourced from real seeded case studies). Replaces the
  // previous unverifiable "127K+ trained" / "$340M+ revenue" stat cards.
  const quotes = [
    { q: t("marketing.about.quote1"), a: t("marketing.about.quote1Author"), c: t("marketing.about.quote1City") },
    { q: t("marketing.about.quote2"), a: t("marketing.about.quote2Author"), c: t("marketing.about.quote2City") },
    { q: t("marketing.about.quote3"), a: t("marketing.about.quote3Author"), c: t("marketing.about.quote3City") },
    { q: t("marketing.about.quote4"), a: t("marketing.about.quote4Author"), c: t("marketing.about.quote4City") },
  ];
  const values = [
    { Icon: Target, title: t("marketing.about.value1Title"), desc: t("marketing.about.value1Desc") },
    { Icon: Zap, title: t("marketing.about.value2Title"), desc: t("marketing.about.value2Desc") },
    { Icon: UsersIcon, title: t("marketing.about.value3Title"), desc: t("marketing.about.value3Desc") },
    { Icon: Heart, title: t("marketing.about.value4Title"), desc: t("marketing.about.value4Desc") },
  ];

  return (
    <PublicShell>
      <Seo title={t("marketing.about.seoTitle")} description={t("marketing.about.seoDescription")} />

      {/* Hero */}
      <Reveal as="section" className="container py-20 md:py-24">
        <SectionHeader
          align="center"
          eyebrow={t("marketing.about.eyebrow")}
          title={t("marketing.about.heroH")}
          lede={t("marketing.about.heroIntro")}
        />
      </Reveal>

      {/* Stats strip */}
      <section className="border-y border-border bg-muted/30">
        <Reveal className="container py-20 md:py-24">
          <SectionHeader
            align="center"
            className="mb-10"
            eyebrow={t("marketing.about.impactLabel")}
            title={t("marketing.about.impactH")}
          />
          <div className="grid gap-6 sm:grid-cols-2">
            {quotes.map((q) => (
              <figure key={q.a} className="rounded-xl border border-border bg-card p-6 text-left">
                <blockquote className="text-sm leading-relaxed text-foreground md:text-base">
                  &ldquo;{q.q}&rdquo;
                </blockquote>
                <figcaption className="mt-4 text-xs text-muted-foreground">
                  <span className="font-semibold text-foreground">{q.a}</span>
                  <span className="mx-1.5 opacity-60">·</span>
                  {q.c}
                </figcaption>
              </figure>
            ))}
          </div>
        </Reveal>
      </section>

      <section className="border-t border-border bg-muted/30">
        <Reveal className="container py-16 md:py-20">
          <div className="mx-auto grid max-w-4xl gap-8 md:grid-cols-2 md:items-center">
            <div>
              <h2 className="t-display-md">{t("marketing.about.missionTitle")}</h2>
              <p className="mt-4 text-muted-foreground">{t("marketing.about.missionBody")}</p>
            </div>
            <Card>
              <CardContent className="flex items-start gap-4 p-6">
                <Avatar className="h-16 w-16">
                  <AvatarFallback>K</AvatarFallback>
                </Avatar>
                <div>
                  <Badge variant="outline" className="mb-2">{t("marketing.about.founderTitle")}</Badge>
                  <div className="text-lg font-semibold">{t("marketing.about.founderName")}</div>
                  <div className="text-sm text-muted-foreground">{t("marketing.about.founderRole")}</div>
                  <p className="mt-3 text-sm">{t("marketing.about.founderBio")}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </Reveal>
      </section>

      {/* Story */}
      <Reveal as="section" className="container py-16 md:py-20">
        <div className="mx-auto max-w-3xl">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">
            {t("marketing.about.storyLabel")}
          </div>
          <h2 className="mt-3 t-display-md">{t("marketing.about.storyH")}</h2>
          <div className="mt-6 space-y-5 text-base leading-relaxed text-muted-foreground">
            <p>{t("marketing.about.storyP1")}</p>
            <p>{t("marketing.about.storyP2")}</p>
            <p>{t("marketing.about.storyP3")}</p>
          </div>
        </div>
      </Reveal>

      {/* Values */}
      <section className="border-y border-border bg-muted/30">
        <Reveal className="container py-16 md:py-20">
          <div className="mx-auto max-w-2xl text-center">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">
              {t("marketing.about.valuesLabel")}
            </div>
            <h2 className="mt-3 t-display-md">{t("marketing.about.valuesH")}</h2>
          </div>
          <div className="mx-auto mt-10 grid max-w-5xl gap-6 sm:grid-cols-2">
            {values.map(({ Icon, title, desc }) => (
              <Card key={title}>
                <CardContent className="p-6">
                  <div className="mb-4 grid h-10 w-10 place-items-center rounded-md bg-primary/10 text-primary">
                    <Icon className="h-5 w-5" strokeWidth={1.75} />
                  </div>
                  <div className="text-lg font-semibold">{title}</div>
                  <p className="mt-2 text-sm text-muted-foreground">{desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </Reveal>
      </section>

      <Reveal as="section" className="container py-16 md:py-24">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="t-display-md">{t("marketing.about.teamTitle")}</h2>
          <p className="mt-3 text-muted-foreground">{t("marketing.about.teamSubtitle")}</p>
        </div>

        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {providers === null && Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-48 rounded-lg" />
          ))}
          {providers && providers.length === 0 && (
            <p className="col-span-full text-center text-muted-foreground">{t("marketing.about.teamEmpty")}</p>
          )}
          {providers?.map((p) => {
            const fullName = [p.profile?.first_name, p.profile?.last_name].filter(Boolean).join(" ") || t("marketing.about.roleOperator", "Operator");
            const initials = fullName.split(/\s+/).filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase()).join("") || "OP";
            const location = [p.profile?.city, p.profile?.country].filter(Boolean).join(", ");
            // Deterministic colour from name hash → HSL hue (theme-aware via primary tint)
            let h = 0; for (const c of fullName) h = (h * 31 + c.charCodeAt(0)) % 360;
            const avatarBg = `hsl(${h} 65% 45%)`;
            return (
              <Card key={p.user_id}>
                <CardContent className="p-6">
                  <div className="flex items-center gap-3">
                    <Avatar>
                      {p.profile?.avatar_url ? (
                        <AvatarImage src={p.profile.avatar_url} alt={fullName} />
                      ) : null}
                      <AvatarFallback style={{ background: avatarBg, color: "white" }}>{initials}</AvatarFallback>
                    </Avatar>
                    <div>
                      <div className="font-semibold">{fullName}</div>
                      {location && <div className="text-xs text-muted-foreground">{location}</div>}
                    </div>
                  </div>
                  <p className="mt-3 text-sm font-medium">{pickHeadline(p, "headline")}</p>
                  <p className="mt-2 line-clamp-3 text-sm text-muted-foreground">{pickBio(p, "bio")}</p>
                  {p.expertise_areas && p.expertise_areas.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1">
                      {p.expertise_areas.slice(0, 3).map((tag) => (
                        <Badge key={tag} variant="secondary" className="text-xs">
                          {t(`marketing.about.tags.${tag}`, tag.replace(/_/g, " "))}
                        </Badge>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </Reveal>

      <section className="border-t border-border">
        <Reveal className="container py-16 text-center">
          <h2 className="t-display-sm">{t("marketing.about.ctaTitle")}</h2>
          <p className="mt-2 text-muted-foreground">{t("marketing.about.ctaSubtitle")}</p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <Button asChild variant="brand">
              <Link to="/contact">{t("marketing.about.ctaButton")} <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.75} /></Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/careers">{t("marketing.about.ctaCareers")}</Link>
            </Button>
          </div>
        </Reveal>
      </section>
    </PublicShell>
  );
}
